import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Button } from '@/components/ui/button'

/** Without `undo` it is a plain notice ("Settings saved"), with no button. */
type ShowUndoToast = (message: ReactNode, undo?: () => void) => void

const UndoToastContext = createContext<ShowUndoToast | null>(null)

/** How long a toast stays up before it auto-dismisses. */
const AUTO_DISMISS_MS = 6000

interface ActiveToast {
  id: number
  message: ReactNode
  undo?: () => void
}

/**
 * One toast, bottom-right, for the list's "safe edits save straight away"
 * pattern (design note §1 anno 6): in-row edits commit immediately and offer
 * Undo instead of an "Are you sure?" before the fact.
 *
 * Mounted once, in `Layout.tsx` around `<Outlet>`, so any screen can call
 * `useUndoToast()` without mounting its own provider. A second toast while one
 * is already showing replaces it rather than stacking — the list only ever
 * fires one edit at a time.
 */
export function UndoToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<ActiveToast | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const nextId = useRef(0)

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
    setToast(null)
  }, [])

  const show = useCallback<ShowUndoToast>((message, undo) => {
    if (timerRef.current) clearTimeout(timerRef.current)
    const id = ++nextId.current
    setToast({ id, message, undo })
    timerRef.current = setTimeout(() => {
      setToast((current) => (current?.id === id ? null : current))
    }, AUTO_DISMISS_MS)
  }, [])

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  return (
    <UndoToastContext.Provider value={show}>
      {children}
      {toast && (
        <div className='pointer-events-none fixed inset-x-0 bottom-0 z-50 flex justify-end p-4 sm:p-6'>
          <div
            role='status'
            className='pointer-events-auto flex max-w-sm items-center gap-4 rounded-lg bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg'
          >
            <span className='min-w-0'>{toast.message}</span>
            {toast.undo && (
              <Button
                type='button'
                size='sm'
                variant='ghost'
                className='h-auto shrink-0 px-1.5 py-0.5 text-sky-300 hover:bg-white/10 hover:text-sky-200'
                onClick={() => {
                  const { undo } = toast
                  dismiss()
                  undo?.()
                }}
              >
                Undo
              </Button>
            )}
          </div>
        </div>
      )}
    </UndoToastContext.Provider>
  )
}

/** Fire a bottom-right toast, with Undo when given one. Must be called under `UndoToastProvider`. */
export function useUndoToast(): ShowUndoToast {
  const show = useContext(UndoToastContext)
  if (!show) throw new Error('useUndoToast must be used within an UndoToastProvider')
  return show
}
